import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PanelBreadcrumbs from './PanelBreadcrumbs';
import Toast from './Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Orders = ({ setActivePage }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [isOrderDetailsLoading, setIsOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState(null);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Customer search states
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const customerSearchTimeoutRef = useRef(null);
  const customerDropdownRef = useRef(null);
  
  // Merchant search states
  const [merchantSearchQuery, setMerchantSearchQuery] = useState('');
  const [merchantSearchResults, setMerchantSearchResults] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [isMerchantDropdownOpen, setIsMerchantDropdownOpen] = useState(false);
  const [isSearchingMerchants, setIsSearchingMerchants] = useState(false);
  const merchantSearchTimeoutRef = useRef(null);
  const merchantDropdownRef = useRef(null);

  useEffect(() => {
    fetchOrders();
  }, [page, limit, status, fromDate, toDate, customerId, merchantId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false);
      }
      if (merchantDropdownRef.current && !merchantDropdownRef.current.contains(event.target)) {
        setIsMerchantDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search customers API call
  const searchCustomers = async (query) => {
    if (!query || query.trim().length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setIsSearchingCustomers(true);
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const response = await axios.get(`${API_BASE_URL}/api/customers/search`, {
        params: {
          q: query.trim(),
          limit: 10
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Expected format: { success: true, data: [{ id, name, email? }, ...] }
      const customers = response.data?.data || [];
      setCustomerSearchResults(Array.isArray(customers) ? customers : []);
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  // Debounced customer search
  useEffect(() => {
    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    if (customerSearchQuery.trim().length >= 2) {
      customerSearchTimeoutRef.current = setTimeout(() => {
        searchCustomers(customerSearchQuery);
      }, 300);
    } else {
      setCustomerSearchResults([]);
    }

    return () => {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
    };
  }, [customerSearchQuery]);

  // Search merchants API call
  const searchMerchants = async (query) => {
    try {
      setIsSearchingMerchants(true);
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      const normalizedQuery = query?.trim() || '';
      const shouldUseSearch = normalizedQuery.length >= 2;

      const response = await axios.get(
        shouldUseSearch ? `${API_BASE_URL}/api/merchants/search` : `${API_BASE_URL}/api/superadmin/merchants`,
        {
          params: shouldUseSearch
            ? {
                q: normalizedQuery,
                limit: 10
              }
            : {
                page: 1,
                limit: 10
              },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Search API: { data: [...] }, Superadmin list API: { merchants: [...] } or { data: [...] }
      const merchants = response.data?.data || response.data?.merchants || [];
      setMerchantSearchResults(Array.isArray(merchants) ? merchants : []);
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error searching merchants:', err);
      setMerchantSearchResults([]);
    } finally {
      setIsSearchingMerchants(false);
    }
  };

  // Debounced merchant search
  useEffect(() => {
    if (merchantSearchTimeoutRef.current) {
      clearTimeout(merchantSearchTimeoutRef.current);
    }

    if (merchantSearchQuery.trim().length >= 2 || isMerchantDropdownOpen) {
      merchantSearchTimeoutRef.current = setTimeout(() => {
        searchMerchants(merchantSearchQuery);
      }, 300);
    } else {
      setMerchantSearchResults([]);
    }

    return () => {
      if (merchantSearchTimeoutRef.current) {
        clearTimeout(merchantSearchTimeoutRef.current);
      }
    };
  }, [merchantSearchQuery, isMerchantDropdownOpen]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token from localStorage
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      // Build query parameters
      const params = {
        page,
        limit,
      };
      
      if (status) params.status = status;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      if (customerId) params.customer_id = parseInt(customerId);
      if (merchantId) params.merchant_id = parseInt(merchantId);
      
      const response = await axios.get(`${API_BASE_URL}/api/superadmin/orders`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle response - adjust based on actual API response structure
      const data = response.data;
      
      if (data.orders && Array.isArray(data.orders)) {
        setOrders(data.orders);
      } else if (Array.isArray(data)) {
        setOrders(data);
      } else if (data.data && Array.isArray(data.data)) {
        setOrders(data.data);
      }
      
      // Handle pagination metadata - check multiple possible formats
      if (data.pagination) {
        setTotalPages(data.pagination.total_pages || data.pagination.totalPages || 1);
        setTotalItems(data.pagination.total || data.pagination.total_items || data.pagination.totalItems || 0);
      } else {
        if (data.total_pages !== undefined) {
          setTotalPages(data.total_pages);
        } else if (data.totalPages !== undefined) {
          setTotalPages(data.totalPages);
        } else if (data.total !== undefined && limit) {
          setTotalPages(Math.ceil(data.total / limit));
        } else {
          setTotalPages(1);
        }
        
        if (data.total !== undefined) {
          setTotalItems(data.total);
        } else if (data.total_items !== undefined) {
          setTotalItems(data.total_items);
        } else if (data.totalItems !== undefined) {
          setTotalItems(data.totalItems);
        } else {
          // If no total provided, estimate from current page data
          const currentItems = data.orders?.length || data.data?.length || (Array.isArray(data) ? data.length : 0);
          setTotalItems(currentItems > 0 ? currentItems : 0);
        }
      }
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.error || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadOrdersCsv = async () => {
    const hasAnyFilter = Boolean(status || fromDate || toDate || customerId || merchantId);
    if (!hasAnyFilter) {
      setToast({ message: 'Please apply at least one filter before downloading CSV.', type: 'error' });
      return;
    }

    try {
      setIsDownloadingCsv(true);
      setError(null);

      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const params = {};
      if (status) params.status = status;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      if (customerId) params.customer_id = parseInt(customerId);
      if (merchantId) params.merchant_id = parseInt(merchantId);

      const response = await axios.get(`${API_BASE_URL}/api/superadmin/orders/download`, {
        params,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const contentDisposition = response.headers['content-disposition'] || '';
      const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
      const fileName = decodeURIComponent(fileNameMatch?.[1] || fileNameMatch?.[2] || `orders-${Date.now()}.csv`);

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }

      let downloadErrorMessage = 'Failed to download orders CSV.';
      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          downloadErrorMessage = parsed?.message || parsed?.error || downloadErrorMessage;
        } catch {
          downloadErrorMessage = downloadErrorMessage;
        }
      } else {
        downloadErrorMessage = err.response?.data?.message || err.response?.data?.error || downloadErrorMessage;
      }

      setToast({ message: downloadErrorMessage, type: 'error' });
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    const colors = {
      paid: 'bg-green-100 text-green-800',
      delivered: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      shipped: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
    };
    return colors[statusLower] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return amount || '$0.00';
  };

  const formatDetailDate = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const getOrderDisplayId = (order) => {
    if (!order) return '-';
    return order.id || order.order_id || `#${order.order_number || '-'}`;
  };

  const openOrderDetails = async (order) => {
    const orderId = order?.id || order?.order_id;
    if (!orderId) {
      setOrderDetailsError('Order ID not available');
      setIsOrderDetailsOpen(true);
      return;
    }

    try {
      setIsOrderDetailsOpen(true);
      setIsOrderDetailsLoading(true);
      setOrderDetailsError(null);
      setSelectedOrderDetails(null);

      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const response = await axios.get(`${API_BASE_URL}/api/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const orderData = response.data?.data || response.data;
      setSelectedOrderDetails(orderData);
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error fetching order details:', err);
      setOrderDetailsError(err.response?.data?.message || err.response?.data?.error || 'Failed to load order details');
    } finally {
      setIsOrderDetailsLoading(false);
    }
  };

  const closeOrderDetails = () => {
    setIsOrderDetailsOpen(false);
    setSelectedOrderDetails(null);
    setOrderDetailsError(null);
    setIsOrderDetailsLoading(false);
  };

  const handleFilterChange = (filterType, value) => {
    // Reset to page 1 when filters change
    setPage(1);
    if (filterType === 'status') setStatus(value);
    if (filterType === 'from') setFromDate(value);
    if (filterType === 'to') setToDate(value);
    if (filterType === 'customer_id') setCustomerId(value);
    if (filterType === 'merchant_id') setMerchantId(value);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerId(customer.id || customer.customer_id || '');
    setCustomerSearchQuery(customer.name || customer.customer_name || customer.email || customer.customer_email || '');
    setIsCustomerDropdownOpen(false);
    setPage(1);
  };

  const handleCustomerSearchChange = (value) => {
    setCustomerSearchQuery(value);
    setIsCustomerDropdownOpen(true);
    if (!value) {
      setSelectedCustomer(null);
      setCustomerId('');
      setCustomerSearchResults([]);
      setPage(1);
    }
  };

  const handleMerchantSelect = (merchant) => {
    setSelectedMerchant(merchant);
    setMerchantId(merchant.id || merchant.merchant_id || '');
    setMerchantSearchQuery(merchant.shop_name || merchant.name || merchant.merchant_name || merchant.email || '');
    setIsMerchantDropdownOpen(false);
    setPage(1);
  };

  const handleMerchantSearchChange = (value) => {
    setMerchantSearchQuery(value);
    setIsMerchantDropdownOpen(true);
    if (!value) {
      setSelectedMerchant(null);
      setMerchantId('');
      setMerchantSearchResults([]);
      setPage(1);
    }
  };

  const clearFilters = () => {
    setPage(1);
    setStatus('');
    setFromDate('');
    setToDate('');
    setCustomerId('');
    setMerchantId('');
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setIsCustomerDropdownOpen(false);
    setSelectedMerchant(null);
    setMerchantSearchQuery('');
    setMerchantSearchResults([]);
    setIsMerchantDropdownOpen(false);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Loading orders...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = Boolean(status || fromDate || toDate || customerId || merchantId);

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="p-6">
      <PanelBreadcrumbs current="Orders" setActivePage={setActivePage} />
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">Manage and track all orders</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleDownloadOrdersCsv}
            disabled={isDownloadingCsv || !hasActiveFilters}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title={!hasActiveFilters ? 'Apply at least one filter to enable CSV download' : 'Download filtered orders CSV'}
          >
            {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
          </button>
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium">
            + New Order
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="relative" ref={customerDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
            <div className="relative">
              <input
                type="text"
                value={customerSearchQuery}
                onChange={(e) => handleCustomerSearchChange(e.target.value)}
                onFocus={() => {
                  if (customerSearchQuery.trim().length >= 2 && customerSearchResults.length > 0) {
                    setIsCustomerDropdownOpen(true);
                  }
                }}
                placeholder="Search customer by name or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {isSearchingCustomers && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
            
            {/* Customer Dropdown */}
            {isCustomerDropdownOpen && customerSearchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                {customerSearchResults.map((customer, index) => (
                  <div
                    key={customer.id || customer.customer_id || index}
                    onClick={() => handleCustomerSelect(customer)}
                    className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">
                      {customer.name || customer.customer_name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {customer.email || customer.customer_email || ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {isCustomerDropdownOpen && customerSearchQuery.trim().length >= 2 && !isSearchingCustomers && customerSearchResults.length === 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  No customers found
                </div>
              </div>
            )}
          </div>
          
          <div className="relative" ref={merchantDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Merchant</label>
            <div className="relative">
              <input
                type="text"
                value={merchantSearchQuery}
                onChange={(e) => handleMerchantSearchChange(e.target.value)}
                onFocus={() => setIsMerchantDropdownOpen(true)}
                placeholder="Search merchant by shop name, name or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {isSearchingMerchants && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
            
            {/* Merchant Dropdown */}
            {isMerchantDropdownOpen && merchantSearchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                {merchantSearchResults.map((merchant, index) => (
                  <div
                    key={merchant.id || merchant.merchant_id || index}
                    onClick={() => handleMerchantSelect(merchant)}
                    className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">
                      {merchant.shop_name || merchant.name || merchant.merchant_name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {merchant.email || merchant.merchant_email || ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {isMerchantDropdownOpen && !isSearchingMerchants && merchantSearchResults.length === 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  No merchants found
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button
              onClick={fetchOrders}
              className="ml-auto px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merchant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order, index) => (
                  <tr key={order.id || order.order_id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.id || order.order_id || `#${order.order_number || index + 1}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.customer_name || order.customer?.name || order.customer_email || order.customer?.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.merchant_name ||
                        order.merchant_shop_name ||
                        order.merchant?.shop_name ||
                        order.merchant?.name ||
                        order.shop_name ||
                        '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(order.total_amount || order.amount || order.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                        {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.created_at || order.order_date || order.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openOrderDetails(order)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        View
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">Edit</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {page} of {totalPages || 1} {totalItems > 0 && `(${totalItems} total items)`}
            {orders.length > 0 && ` - Showing ${orders.length} items`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {page} / {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={page >= (totalPages || 1) || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>

        {/* Items per page selector */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Items per page:
          </div>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {isOrderDetailsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Order Details</h2>
                <p className="text-sm text-gray-500">View complete order information</p>
              </div>
              <button
                onClick={closeOrderDetails}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                title="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(90vh-73px)] overflow-y-auto p-6">
              {isOrderDetailsLoading ? (
                <div className="flex h-64 items-center justify-center">
                  <div className="flex flex-col items-center">
                    <div className="mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                    <p className="text-gray-600">Loading order details...</p>
                  </div>
                </div>
              ) : orderDetailsError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                  {orderDetailsError}
                </div>
              ) : selectedOrderDetails ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Order ID</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{getOrderDisplayId(selectedOrderDetails)}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Status</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(selectedOrderDetails.status)}`}>
                          {selectedOrderDetails.status ? selectedOrderDetails.status.charAt(0).toUpperCase() + selectedOrderDetails.status.slice(1) : '-'}
                        </span>
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Total Amount</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{formatCurrency(selectedOrderDetails.total_amount)}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Customer Name</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{selectedOrderDetails.customer_name || '-'}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Customer Email</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{selectedOrderDetails.customer_email || '-'}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Merchant Name</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{selectedOrderDetails.merchant_name || '-'}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Created At</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{formatDetailDate(selectedOrderDetails.created_at)}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Updated At</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{formatDetailDate(selectedOrderDetails.updated_at)}</div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200">
                    <div className="border-b border-gray-200 px-5 py-4">
                      <h3 className="text-base font-semibold text-gray-900">Order Items</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Product</th>
                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Quantity</th>
                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Price at Purchase</th>
                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Line Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {Array.isArray(selectedOrderDetails.items) && selectedOrderDetails.items.length > 0 ? (
                            selectedOrderDetails.items.map((item, index) => (
                              <tr key={item.id || index} className="hover:bg-gray-50">
                                <td className="px-5 py-4 text-sm text-gray-900">{item.product_name || '-'}</td>
                                <td className="px-5 py-4 text-sm text-gray-700">{item.quantity ?? '-'}</td>
                                <td className="px-5 py-4 text-sm text-gray-700">{formatCurrency(item.price_at_purchase)}</td>
                                <td className="px-5 py-4 text-sm text-gray-700">
                                  {typeof item.quantity === 'number' && typeof item.price_at_purchase === 'number'
                                    ? formatCurrency(item.quantity * item.price_at_purchase)
                                    : '-'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="px-5 py-8 text-center text-sm text-gray-500">
                                No items found for this order.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Orders;
